import { Button } from "../../../ui/Button";
import { LAYOUT, TEXT_STYLES } from "../../../constants";
import { RunState, newRun } from "../../../state/runState";
import { EncounterScene } from "../../EncounterScene";
import { BaseScreen } from "../../../screens/BaseScreen";
import { createRunInfoPanel } from "../../../screens/uiHelpers";
import { tagTextForAutomation } from "../../../automation/automationTags";
import { OverworldState, newOverworldState } from "../../../state/overworldState";
import { generateBossEncounter } from "../../../state/encounterGeneration";
import { OVERWORLD_SCENE_KEY } from "../../OverworldScene";
import { navLog } from "../../../debugNavigation";
import type { ScreenContext } from "../../../screens/types";

export class BossPromptScreen extends BaseScreen {
  private run!: RunState;
  private overworldState?: OverworldState;

  constructor(scene: EncounterScene) {
    super(scene, "BOSS_PROMPT");
  }

  enter(context?: ScreenContext) {
    this.run = context?.run ?? newRun();
    this.overworldState = context?.overworldState ?? this.scene.getOverworldState?.();

    const { width, height } = this.scene.scale;

    const title = this.scene.add
      .text(width / 2, LAYOUT.screenPadding, "Floor Complete!", TEXT_STYLES.title)
      .setOrigin(0.5, 0);
    this.addToContainer(title);
    tagTextForAutomation(title, "boss-prompt-title");

    const subtitle = this.scene.add
      .text(width / 2, LAYOUT.screenPadding + 56, "Boss Approaching...", TEXT_STYLES.subtitle)
      .setOrigin(0.5, 0);
    this.addToContainer(subtitle);
    tagTextForAutomation(subtitle, "boss-prompt-subtitle");

    const panel = createRunInfoPanel(
      this.scene,
      this.run,
      LAYOUT.screenPadding,
      height * 0.22,
      width - LAYOUT.screenPadding * 2
    );
    this.addToContainer(panel);

    const buttonWidth = width - LAYOUT.screenPadding * 2;

    const confirmButton = new Button(this.scene, LAYOUT.screenPadding, height * 0.7, {
      width: buttonWidth,
      height: LAYOUT.buttonHeight,
      label: "Face the Boss",
      automationName: "fight-boss",
      onClick: () => {
        this.startBossEncounter();
      },
    });
    this.addToContainer(confirmButton);
  }

  private startBossEncounter() {
    // Generate boss encounter config
    const bossConfig = generateBossEncounter(this.run);
    if (!bossConfig) {
      // Fallback: return to overworld if boss generation fails
      this.returnToOverworld();
      return;
    }

    // Update overworld state with current run
    const overworldState = this.overworldState ?? newOverworldState(this.run);
    overworldState.run = this.run;

    // Transition to encounter screen with boss config
    this.scene.overlay.transition(() => {
      this.scene.screenManager.setScreen("ENCOUNTER", {
        run: this.run,
        enemyConfig: bossConfig,
        overworldState,
      });
    });
  }

  private returnToOverworld() {
    const overworldState = this.overworldState ?? newOverworldState(this.run);
    overworldState.run = this.run;

    this.scene.overlay.transition(() => {
      navLog("BossPromptScreen", "Starting overworld after boss prompt fallback.", {
        floor: this.run.floor,
        isBossEncounter: Boolean(this.run.isBossEncounter),
      });
      this.scene.scene.start(OVERWORLD_SCENE_KEY, {
        overworldState,
      });
    });
  }
}
